import React, { startTransition, useEffect, useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { get, isNull, isEmpty, isArray, flow, noop } from 'lodash-es';
import { connect } from 'react-redux';
/* Utils */
import { bindToPath, connectToModel } from 'client/data/luckdragon/redux/react-binding';
import { isUsed } from 'site-modules/shared/utils/inventory-utils/is-used';
import {
  getLeaseMonthlyPayment,
  isMonthlyPaymentUpdating,
  getMonthlyPayment,
} from 'site-modules/inventory/components/price-summary/price-summary-utils';
import {
  isPartnerDealer,
  isCarMax,
  isPrivateParty,
  isCarZingPartner,
  isCarZingBasic,
} from 'site-modules/shared/utils/dealer-details-utils';
import { getPriceCheckerPromoAvailability } from 'site-modules/shared/utils/inventory/get-price-checker-promo-availability';
import { getPriceGraphAvailability } from 'site-modules/shared/utils/inventory/get-price-graph-availability';
import { isNew } from 'site-modules/shared/utils/inventory-utils/is-new';
import { EventToolbox } from 'client/utils/event-toolbox';
import { getTransactionCountCopyValues } from 'site-modules/shared/utils/price-checker';
import {
  isEligible,
  getSelectedOffer,
  isCapOneProvider,
  countersTriggeredAllTerms,
  isAvailableLocation,
} from 'site-modules/shared/utils/financing/financing';
import { getTotalRebates } from 'site-modules/shared/components/inventory/utils/get-total-rebates';
import { EngagementHandler } from 'client/engagement-handlers';
import { DigitalCheckoutEngagementHandler } from 'client/engagement-handlers/digital-checkout-engagement-handler/digital-checkout-engagement-handler';
import { FinancingEngagementHandler } from 'client/engagement-handlers/financing-engagement-handler/financing-engagement-handler';
import { createDeal } from 'site-modules/shared/utils/financing/create-caramel-deal';
import { isCaramelEligible } from 'site-modules/shared/utils/financing/caramel-utils';
import { ADVERTISED_PRICE_SOURCE } from 'site-modules/shared/constants/inventory/prices';
import { getEdmundsSuggestedPrice } from 'site-modules/shared/utils/inventory-utils/prices';
import { isLeaseEnabled, LEASE_PAYMENT_DATA_PATH } from 'site-modules/shared/utils/inventory-utils/is-lease-enabled';
import { isTopEmbeddedLeadFormEnabled as isTopEmbeddedLeadFormEnabledUtil } from 'site-modules/shared/utils/inventory/build-lead-target-preload-enabled';
import { openLinkInNewTab } from 'site-modules/shared/utils/open-link-in-new-tab';
import { ExperimentUtil } from 'client/utils/experiment/experiment-util';
import { getPrices } from 'site-modules/shared/utils/inventory-utils/get-prices';
/* Models */
import { InventoryEntities } from 'client/data/models/inventory';
import { Incentive } from 'site-modules/shared/constants/car-buying/models';
import { DealerInfoModel } from 'client/data/models/dealer-info';
import { buildTransactionCountPath, VehicleEntities, VehicleModel } from 'client/data/models/vehicle-v2';
import { FinancingModel, PATHS } from 'client/data/models/financing';
import { VisitorModel } from 'client/data/models/visitor';
import { RequestModel } from 'client/data/models/request';
import { InventoryIncentivesEntities } from 'client/data/models/inventory-incentives';
/* Components */
import { ReportSmthWrongLink } from 'site-modules/shared/components/report-smth-wrong-link/report-smth-wrong-link';
import { VDPEmbeddedLeadForm } from 'site-modules/shared/components/inventory/vdp-embedded-lead-form/vdp-embedded-lead-form';
import { PriceRow } from 'site-modules/inventory/components/price-summary/price-row';
import { PriceSummaryBannerGraph } from 'site-modules/inventory/components/price-summary-banner-graph/price-summary-banner-graph';
import { PriceCheckerChartPromoVdp } from 'client/site-modules/shared/components/price-checker/price-checker-chart/price-checker-chart-promo-vdp';
import { CapitalOneEntryPoint } from 'site-modules/shared/components/financing/capital-one-entry-point/capital-one-entry-point';
import { VdpModule } from 'site-modules/shared/components/inventory/vdp-module/vdp-module';
import { CaramelEntryPoint } from 'client/site-modules/shared/components/financing/caramel/caramel-entry-point/caramel-entry-point';
import { Experiment, Recipe } from 'site-modules/shared/components/experiment';
import { ZipLongDistanceConfirmProvider } from 'site-modules/shared/components/lead-form/zip-long-distance-confirm/zip-long-distance-confirm-provider';
import { withIncentives } from 'site-modules/shared/components/inventory/with-incentives/with-incentives';
import { CAFPrequalifyEntryPoint } from 'site-modules/shared/components/financing/caf-prequalify-entry-point/caf-prequalify-entry-point';
import { SellerFeesDisclaimer } from 'site-modules/shared/components/inventory/seller-fees-disclaimer/seller-fees-disclaimer';
import { ContactDealer } from 'site-modules/shared/components/inventory/contact-dealer/contact-dealer';
import { ContactSeller } from 'client/site-modules/shared/components/private-party/contact-seller/contact-seller';
import { CaramelDealStatusBadge } from 'site-modules/shared/components/financing/caramel/components/caramel-deal-status-badge/caramel-deal-status-badge';

/* Constants */
import { CALCULATOR_TYPE_LOAN, CALCULATOR_TYPE_LEASE } from 'site-modules/shared/constants/calculator/calculator';
import { BOL_VDP_DEALERS } from 'site-modules/shared/constants/inventory/buy-online-dealers';
import { DATA_PATH } from 'client/engagement-handlers/inventory-engagement-handler/constants';
import { DIGITAL_CHECKOUT_EVENTS } from 'site-modules/shared/constants/financing/digital-checkout-events';
import { LISTING_STATUS_APPROVED } from 'site-modules/shared/constants/private-party/car-details';
import { PRIVATE_PARTY_EVENTS } from 'site-modules/shared/constants/private-party/private-party-events';
import { CREATIVE_ID_SUMMARY_CHECKOUT } from 'site-modules/shared/constants/private-party/tracking';
import { CARAMEL_MAKE_AN_OFFER_CTA } from 'site-modules/shared/constants/financing/caramel';

import './price-summary.scss';

EngagementHandler.register(DigitalCheckoutEngagementHandler);
EngagementHandler.register(FinancingEngagementHandler);

const LOAN_PAYMENT_DATA_PATH = 'prices.loan.payment';

function PriceSummaryUI({
  vinData = {},
  isMobile = false,
  isLeaseView = false,
  calculatorPayments = {},
  radius = null,
  embedLeadFormParams = {},
  pageNameLegacy = '',
  tcpaParentDealershipName = null,
  isDealerCarcodeSuspended = false,
  forceLease = false,
  isBasicViewEnabled = false,
  isVlaPage = false,
  isVdpPriceCheckerEnabled = false,
  showPriceChart = true,
  showContactDealer = true,
  showReportLink = true,
  isVdp = false,
  isPrequalified = false,
  transactionCount = null,
  isProdEnv = false,
  calculatorSelections = {},
  isFinancingError = false,
  financingOffers = null,
  visitorStateCode = '',
  dealer = null,
  protocol = '',
  hostName = '',
  selectedLeaseIncentives = [],
  selectedLoanIncentives = [],
  primaryLoanIncentives = [],
  hideLeadForm = false,
  hideLeadFormForDrivewayDealer = false,
  ted6504Recipe = 'ctrl',
  enableCaramel = false,
  isForceDefault = false,
  location = {},
  privateListingData = null,
  handlePrivatePartyMakeOffer = noop,
  shop4713Recipe = 'ctrl',
  isDealerFeesEnabled = false,
  isSimulatedLeadForm = false,
}) {
  const [isLoading, setIsLoading] = useState(isPrequalified);

  const isPartner = isPartnerDealer(vinData);
  const isPrivatePartyListing = isPrivateParty(vinData);

  const handleCaramelMakeOffer = async () => {
    if (isPrivatePartyListing) {
      EventToolbox.fireCustomEvent(PRIVATE_PARTY_EVENTS.SUMMARY_CHECKOUT, {
        creativeId: CREATIVE_ID_SUMMARY_CHECKOUT,
        value: CARAMEL_MAKE_AN_OFFER_CTA,
      });

      return handlePrivatePartyMakeOffer();
    }
    const deal = await createDeal({ dealer, vinData, protocol, hostName });
    const shareLink = deal?.shareLink;

    EventToolbox.fireCustomEvent(DIGITAL_CHECKOUT_EVENTS.CREATE_DEAL, { deal, isPartner });
    if (shareLink) {
      openLinkInNewTab(shareLink);
    }

    return '';
  };

  useEffect(() => {
    if (isPrequalified && !isNull(financingOffers) && isLoading) {
      startTransition(() => setIsLoading(false));
    }
  }, [financingOffers, isLoading, isPrequalified]);

  const { type, prices, computedDisplayInfo } = vinData;
  const isUsedType = isUsed(type);

  const {
    advertisedPrice,
    advertisedPriceSource,
    msrpPrice,
    priceStrikethrough,
    isDealTypeAvailable,
    dealType,
    isDisplayBelowAboveMarketCopy,
    savings,
  } = getPrices(prices, computedDisplayInfo, type);

  const dealTypeLowerCase = dealType.toLowerCase();

  const stateCode = get(vinData, 'dealerInfo.address.stateCode', '');

  const isCapOne = isCapOneProvider(vinData);
  const isCounterTriggered = isArray(financingOffers) && countersTriggeredAllTerms(financingOffers);

  const isEligibleVIN = isEligible(vinData);
  const isCaramelEligibleVIN = isUsedType && dealer?.caramelOptIn && isCaramelEligible(vinData) && enableCaramel;

  const isCapOnePrequalified =
    isUsedType &&
    isCapOne &&
    isEligibleVIN &&
    isPrequalified &&
    !isEmpty(financingOffers) &&
    !isFinancingError &&
    !isCounterTriggered;

  const selectedOffer = getSelectedOffer(financingOffers, calculatorSelections);
  const financingOffer = isCapOnePrequalified
    ? {
        ...calculatorSelections,
        ...get(selectedOffer, 'financeInformation', {}),
        zipCode: get(selectedOffer, 'vehicleStructure.taxesAndFees.zipCode', ''),
      }
    : financingOffers;

  const showLeasePayments = isLeaseView;
  const selectedIncentives = showLeasePayments ? selectedLeaseIncentives : selectedLoanIncentives;
  const totalRebate = useMemo(
    () =>
      getTotalRebates({
        incentives: selectedIncentives,
        showLeasePayments,
      }),
    [selectedIncentives, showLeasePayments]
  );

  const calculatorLeasePayment = get(calculatorPayments, `${CALCULATOR_TYPE_LEASE}.widgetMonthlyPayment`);
  const calculatorLoanPayment = get(calculatorPayments, `${CALCULATOR_TYPE_LOAN}.widgetMonthlyPayment`);

  const isMonthlyPaymentLoading = isMonthlyPaymentUpdating({
    showLeasePayments,
    calculatorLeasePayment,
    calculatorLoanPayment,
    isCapOnePrequalified,
    payment: get(financingOffer, 'monthlyPayment'),
  });

  const payment = getMonthlyPayment(vinData, LOAN_PAYMENT_DATA_PATH, calculatorLoanPayment);
  const leasePayment = getLeaseMonthlyPayment(vinData, LEASE_PAYMENT_DATA_PATH, calculatorLeasePayment);
  const displayLeasePayment = Boolean(forceLease && leasePayment);

  const monthlyPaymentRowLabel = showLeasePayments ? 'Est. lease' : 'est. loan';

  const monthlyPayment = showLeasePayments ? leasePayment : payment;
  const useMonthlyPaymentWithTaxesAndFees = get(calculatorPayments, 'useMonthlyPaymentWithTaxesAndFees');
  const calculatorType = showLeasePayments ? CALCULATOR_TYPE_LEASE : CALCULATOR_TYPE_LOAN;
  const monthlyPaymentValue = useMonthlyPaymentWithTaxesAndFees
    ? get(calculatorPayments, `${calculatorType}.monthlyWithTaxes`) || monthlyPayment
    : monthlyPayment;
  const isMsrpFeesIncluded =
    isDealerFeesEnabled && advertisedPriceSource === ADVERTISED_PRICE_SOURCE.DEALER_MSRP_FEES_INCLUDED;

  const dataTrackingParent = `${isUsedType ? 'used' : 'new'}-summary_${isPrivatePartyListing ? 'sellerinfo' : 'phonenum'}-${pageNameLegacy}`;
  const wrapperDataAttr = !isDealerCarcodeSuspended && !isMobile ? { 'data-hidedelighted': true } : {};

  const isPriceSummaryBannerGraphAvailable = getPriceGraphAvailability(vinData, location);
  const chart = getPriceCheckerPromoAvailability(vinData) && (
    <div>
      <PriceCheckerChartPromoVdp vehicle={vinData} isMobile={isMobile} isAnchorLink={isVdpPriceCheckerEnabled} />
    </div>
  );

  const edmundsSuggestedPrice = getEdmundsSuggestedPrice({
    vinData,
    selectedIncentives,
    primaryLoanIncentives,
    showLeasePayments,
  });
  const hasBestPrice = !isUsedType && !!edmundsSuggestedPrice;
  const showDealerPriceAndHighESP = !isUsedType && !!advertisedPrice && edmundsSuggestedPrice > advertisedPrice;
  const isHighESP = (!isUsedType && !isVlaPage && edmundsSuggestedPrice > advertisedPrice) || showDealerPriceAndHighESP;

  const parentDealershipName = get(vinData, DATA_PATH.PARENT_DEALERSHIP_NAME, '');
  const partnerCode = get(vinData, 'dealerInfo.partnerCode', '');
  const isCarmaxDealer = isCarMax(parentDealershipName);
  const isCarZingDealer = isCarZingPartner(partnerCode);
  const isCarZingBasicDealer = isCarZingBasic(partnerCode);
  const bolEnabledDealer = BOL_VDP_DEALERS.includes(parentDealershipName);
  const isTED6504Enabled = ['chal-1', 'chal-2'].includes(ted6504Recipe) && isCarmaxDealer;
  const isCarZingPrequalify = isCarZingDealer && isUsedType;

  const reportSmthWrongLink = () =>
    showReportLink && (
      <div className="mt-1 small">
        <ReportSmthWrongLink
          linkClassName="align-middle text-decoration-none-hover text-primary-darker py-0_5"
          iconClassName="icon-notification2 me-0_5 text-gray-dark"
          title="Report a problem"
          form="see-something-wrong-with-this-listing"
          trackingValue="report a problem"
          trackingId="other_site_clickout"
        />
      </div>
    );

  const dealerState = get(vinData, 'dealerInfo.address.stateName', '');
  const isCapOneAvailable = isAvailableLocation(visitorStateCode) && isUsedType && isVdp;
  const isCapOneIneligible = isCapOneAvailable && !isEligibleVIN;

  const isTopEmbeddedLeadFormEnabled = isTopEmbeddedLeadFormEnabledUtil(vinData, isSimulatedLeadForm, location);

  const contactDealerProps = {
    vinData,
    isSimulatedLeadForm,
    location,
    showLeasePayments: isLeaseEnabled(location),
    radius,
    isTopEmbeddedLeadFormEnabled,
    tcpaParentDealershipName,
    isDealerCarcodeSuspended,
    isTED6504Enabled,
    isVlaPage,
    isDealerWebsiteLinkHidden: isVlaPage || !isPartner,
    displayPrice: hasBestPrice ? edmundsSuggestedPrice : advertisedPrice,
    isProdEnv,
    hideLeadForm: hideLeadFormForDrivewayDealer,
    ted6504Recipe,
    isMobile,
    dataTrackingParent,
    dealer,
    isCapOneAvailable,
    isCapOneIneligible: isCapOneIneligible && !isPartner,
    handleCaramelMakeOffer,
    isCaramelOptInDealer: isCaramelEligibleVIN,
    isCarZingPrequalify,
    isForceDefault,
  };

  const nonPrequalifiedCapOneEntryPoint = !isPrequalified && !isTED6504Enabled && !isCarZingPrequalify && (
    <CapitalOneEntryPoint
      vinData={vinData}
      isCapOneIneligible={isCapOneIneligible}
      shop4713Recipe={shop4713Recipe}
      isSimulatedLeadForm={isSimulatedLeadForm}
    />
  );
  const prequalifiedCapOneEntryPoint = isPrequalified &&
    isCapOneAvailable &&
    isEligibleVIN &&
    !isTED6504Enabled &&
    !isCarZingPrequalify && (
      <CapitalOneEntryPoint
        vinData={vinData}
        isPrequalified={isPrequalified}
        isFinancingError={isFinancingError}
        financingOffer={financingOffer}
        isLoading={isLoading}
        isCounterTriggered={isCounterTriggered}
      />
    );
  const dealerPriceDisclaimer = vinData?.prices.dealerPriceDisclaimer;

  const contactSellerProps = {
    sellerLocation: [privateListingData?.sellerCity, privateListingData?.sellerState].filter(Boolean).join(', '),
    vehicleTitle: [privateListingData?.year, privateListingData?.make, privateListingData?.model]
      .filter(Boolean)
      .join(' '),
    vehiclePrice: privateListingData?.internetPrice,
    onSubmitOffer: handleCaramelMakeOffer,
    offerDealStatus: dealer?.dealStatus,
    listingId: privateListingData?.listingId,
  };

  const financingEntryPoint = (isEmbedded = false) => {
    const caramelModule = enableCaramel && (
      <CaramelEntryPoint
        isEmbedded={isEmbedded}
        handleCaramelMakeOffer={handleCaramelMakeOffer}
        isPartner={isPartner}
        dealer={dealer}
        isPrivateListing={isPrivatePartyListing}
      />
    );

    const caramelModuleWithExperiment = (
      <Experiment name="TED-7485-FSBO-Messaging" showDefault>
        <Recipe name="ctrl" isDefault>
          {caramelModule}
        </Recipe>
        <Recipe name="chal-0">{caramelModule}</Recipe>
        <Recipe name="chal-1">
          {caramelModule}
          <ContactSeller isAskQuestionOnly {...contactSellerProps} />
        </Recipe>
        <Recipe name="chal-2">
          <ContactSeller isAskQuestionOnly {...contactSellerProps} />
          {caramelModule}
        </Recipe>
        <Recipe name="chal-3">
          <ContactSeller {...contactSellerProps} />
        </Recipe>
      </Experiment>
    );

    if (isPrivatePartyListing) {
      return privateListingData?.listingStatus === LISTING_STATUS_APPROVED ? caramelModuleWithExperiment : null;
    }
    return isCaramelEligibleVIN && !isNull(dealer) ? caramelModule : nonPrequalifiedCapOneEntryPoint;
  };

  const showTopContactDealer = showContactDealer && bolEnabledDealer && isPartner && !isVlaPage;
  const showBottomContactDealer = showContactDealer && (!bolEnabledDealer || !isPartner || isVlaPage);
  const showFinancingEntryPoint = isUsedType && !isPartner && !isSimulatedLeadForm;
  const dealStatus = dealer?.dealStatus;

  return (
    <div
      className="price-summary mb-1_5"
      data-tracking-parent={`${dataTrackingParent}${isCarZingPrequalify ? '-carzing_prequal' : ''}`}
      {...wrapperDataAttr}
    >
      <VdpModule
        withShadow
        withIndents={false}
        className={classNames('bg-white overflow-hidden', {
          'with-gradient': !isDealTypeAvailable,
          [`${dealTypeLowerCase} with-double-gradient`]: isDealTypeAvailable,
        })}
        style={{ ...(isCaramelEligibleVIN && isUsedType && !isPartner && { overflow: 'hidden' }) }}
      >
        <div>
          {isUsedType && !isPartner && !isCarZingBasicDealer && !isPrivatePartyListing && (
            <p className="text-center text-cool-gray-30 size-12 border-bottom bg-white px-1_5 pt-0_75 pb-0_5 mb-0">
              Edmunds has created this vehicle listing from available information but it is not dealer-verified. Contact
              this dealer to confirm details.
            </p>
          )}
          <div className={classNames('pt-1_5 px-1_5 rounded-12', { [dealTypeLowerCase]: isDealTypeAvailable })}>
            <PriceRow
              msrpPrice={msrpPrice}
              advertisedPrice={advertisedPrice}
              isDealerMsrpAdvertisedPriceSource={advertisedPriceSource === ADVERTISED_PRICE_SOURCE.DEALER_MSRP}
              hasBestPrice={hasBestPrice}
              isHighESP={isHighESP}
              showDealerPriceAndHighESP={showDealerPriceAndHighESP}
              bestDealPrice={edmundsSuggestedPrice}
              monthlyPaymentValue={get(financingOffer, 'monthlyPayment', monthlyPaymentValue)}
              monthlyPaymentLabel={monthlyPaymentRowLabel}
              isLoading={!isVlaPage && isMonthlyPaymentLoading}
              showDealType={isDealTypeAvailable}
              dealType={dealType}
              isDisplayBelowAboveMarketCopy={isDisplayBelowAboveMarketCopy}
              savings={savings}
              stateCode={stateCode}
              type={type}
              priceStrikethrough={isMsrpFeesIncluded ? false : priceStrikethrough}
              isMobile={isMobile}
              displayLeasePayment={displayLeasePayment}
              forceLease={forceLease}
              isBasicViewEnabled={isBasicViewEnabled}
              isVlaPage={isVlaPage}
              dataTrackingParent={dataTrackingParent}
              isVdpPriceCheckerEnabled={isVdpPriceCheckerEnabled}
              totalRebate={totalRebate}
              transactionCountCopyValues={getTransactionCountCopyValues({
                transactionCount,
                styleParams: {
                  trim: get(vinData, 'vehicleInfo.styleInfo.trim', ''),
                  makeName: get(vinData, 'vehicleInfo.styleInfo.make', ''),
                  modelName: get(vinData, 'vehicleInfo.styleInfo.model', ''),
                },
                dealerState,
                isVdpPriceChecker: true,
              })}
              hideMonthlyPayment={isPrivatePartyListing}
              isMsrpFeesIncluded={isMsrpFeesIncluded}
              isDealerFeesEnabled={isDealerFeesEnabled}
              isPartner={isPartner}
              vinData={vinData}
              isLeaseView={isLeaseView}
            />
            {showPriceChart &&
              (isPriceSummaryBannerGraphAvailable ? <PriceSummaryBannerGraph vinData={vinData} /> : chart)}
            {!!dealerPriceDisclaimer && advertisedPrice && (
              <SellerFeesDisclaimer disclaimer={dealerPriceDisclaimer} className="mt-1 mb-1_5" />
            )}
          </div>
          <div className="px-1_5 pb-1_5 overflow-hidden pos-r">
            {showTopContactDealer && <ContactDealer {...contactDealerProps} />}

            {(isCarZingPrequalify || isTED6504Enabled) && !isVlaPage && (
              <CAFPrequalifyEntryPoint
                vinData={vinData}
                ted6504Recipe={ted6504Recipe}
                isProdEnv={isProdEnv}
                isForceDefault={isForceDefault}
                isCarZingPrequalify={isCarZingPrequalify}
              />
            )}

            {showFinancingEntryPoint && financingEntryPoint(true)}

            {showFinancingEntryPoint && dealStatus && (
              <CaramelDealStatusBadge dealStatus={dealStatus} isPrivateListing={isPrivatePartyListing} />
            )}

            {isTopEmbeddedLeadFormEnabled &&
              !hideLeadForm &&
              (isVdp && isPartner ? (
                <ZipLongDistanceConfirmProvider>
                  <VDPEmbeddedLeadForm
                    className="price-summary-embedded-form px-1_5 rounded-bottom mt-1_5 no-border-top"
                    {...embedLeadFormParams}
                    styles={{ margin: '-24px' }}
                  />
                </ZipLongDistanceConfirmProvider>
              ) : (
                <VDPEmbeddedLeadForm
                  className="price-summary-embedded-form px-1_5 rounded-bottom mt-1_5 no-border-top"
                  {...embedLeadFormParams}
                  styles={{ margin: '-24px' }}
                />
              ))}
            {isUsedType && !isCaramelEligibleVIN && prequalifiedCapOneEntryPoint}
            {showBottomContactDealer && <ContactDealer {...contactDealerProps} />}
          </div>
        </div>
      </VdpModule>

      {((isUsedType && isPartner) || isSimulatedLeadForm) && financingEntryPoint()}
      {(isPartner || isUsedType) && reportSmthWrongLink()}
    </div>
  );
}

PriceSummaryUI.propTypes = {
  vinData: InventoryEntities.InventoryVin,
  isMobile: PropTypes.bool,
  calculatorPayments: PropTypes.shape({}),
  isLeaseView: PropTypes.bool,
  radius: PropTypes.number,
  embedLeadFormParams: PropTypes.shape({}),
  selectedLeaseIncentives: PropTypes.arrayOf(Incentive),
  selectedLoanIncentives: PropTypes.arrayOf(InventoryIncentivesEntities.Incentive),
  primaryLoanIncentives: PropTypes.arrayOf(InventoryIncentivesEntities.Incentive),
  pageNameLegacy: PropTypes.string,
  tcpaParentDealershipName: PropTypes.string,
  isDealerCarcodeSuspended: PropTypes.bool,
  forceLease: PropTypes.bool,
  isBasicViewEnabled: PropTypes.bool,
  isVlaPage: PropTypes.bool,
  isVdpPriceCheckerEnabled: PropTypes.bool,
  showPriceChart: PropTypes.bool,
  showContactDealer: PropTypes.bool,
  showReportLink: PropTypes.bool,
  isVdp: PropTypes.bool,
  isPrequalified: PropTypes.bool,
  transactionCount: VehicleEntities.StyleTransactionCount,
  isProdEnv: PropTypes.bool,
  calculatorSelections: PropTypes.shape({}),
  isFinancingError: PropTypes.bool,
  financingOffers: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.shape({})), PropTypes.shape({})]),
  visitorStateCode: PropTypes.string,
  dealer: PropTypes.shape({ dealStatus: PropTypes.string }),
  protocol: PropTypes.string,
  hostName: PropTypes.string,
  hideLeadForm: PropTypes.bool,
  hideLeadFormForDrivewayDealer: PropTypes.bool,
  ted6504Recipe: PropTypes.string,
  enableCaramel: PropTypes.bool,
  isForceDefault: PropTypes.bool,
  location: PropTypes.shape({}),
  privateListingData: PropTypes.shape({}),
  handlePrivatePartyMakeOffer: PropTypes.func,
  isDealerFeesEnabled: PropTypes.bool,
  shop4713Recipe: PropTypes.string,
  isSimulatedLeadForm: PropTypes.bool,
};

export const stateToPropsConfig = {
  isDealerCarcodeSuspended: bindToPath(
    ({ vinData }) => {
      const rooftopId = get(vinData, 'dealerInfo.rooftopId', '');
      return rooftopId ? `carcodeConfig.${rooftopId}` : '';
    },
    DealerInfoModel,
    ({ smsNumber }) => !smsNumber
  ),
  transactionCount: bindToPath(({ vinData }) => {
    const styleId = get(vinData, 'vehicleInfo.styleInfo.styleId', '');
    return isNew(vinData.type) && !!styleId && buildTransactionCountPath(styleId);
  }, VehicleModel),
  calculatorSelections: bindToPath(
    ({ vinData, isPrequalified }) =>
      isPrequalified && isCapOneProvider(vinData) ? PATHS.CALCULATOR_SELECTIONS({ vin: get(vinData, 'vin') }) : '',
    FinancingModel
  ),
  visitorStateCode: bindToPath('location.stateCode', VisitorModel),
  protocol: bindToPath('protocol', RequestModel),
  hostName: bindToPath('hostName', RequestModel),
};

export const mapStateToProps = state => ({
  pageNameLegacy: get(state, 'pageContext.legacy.pageName') || get(state, 'pageContext.page.name'),
  isProdEnv: get(state, 'venomEnv') === 'prod',
  isForceDefault: state?.forceDefault,
  location: get(state, 'pageContext.location'),
  shop4713Recipe: ExperimentUtil.getForcedOrAssignedRecipeName({
    state,
    campaignName: 'shop-4713-prequal2',
    defaultVal: 'ctrl',
  }),
});

export const PriceSummary = flow(
  component => connectToModel(component, stateToPropsConfig),
  withIncentives,
  connect(mapStateToProps)
)(PriceSummaryUI);
